import { useCallback, useEffect, useRef, useState } from "react";
import { getZonesInBbox } from "@/lib/api";
import type { BBox } from "./useSpots";

/**
 * Fetch no-consumption zones within the map viewport (spec 06 §3).
 *
 * Two-level optimization to avoid slow PostGIS queries on large viewports:
 * 1. Viewport padding: fetch a larger area than visible (1.5×) so small pans
 *    don't trigger a refetch — the data is already cached in state.
 * 2. Request dedup: only fetch when the new viewport center moves beyond
 *    a threshold from the last fetched center.
 *
 * Zones are only shown at zoom >= 12 (MapView layer minzoom), so we skip
 * fetching entirely for very wide viewports where zones wouldn't render.
 */
export interface UseZones {
  zones: GeoJSON.FeatureCollection | null;
  loading: boolean;
  setBounds: (b: BBox | null) => void;
}

const DEBOUNCE_MS = 600;
/** Minimum viewport width (degrees) to bother fetching. Below this, skip. */
const MAX_VIEWPORT_WIDTH = 2.0;
/** How far the center must move (degrees) to trigger a refetch. */
const REFETCH_THRESHOLD = 0.05;

export function useZones(): UseZones {
  const [bounds, setBoundsState] = useState<BBox | null>(null);
  const [zones, setZones] = useState<GeoJSON.FeatureCollection | null>(null);
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);
  /** Last successfully fetched viewport — used for cache comparison. */
  const lastFetchedCenter = useRef<{ lng: number; lat: number } | null>(null);

  const fetchZones = useCallback(async (b: BBox) => {
    // Skip if viewport is too wide (zones won't render anyway, query would timeout)
    const width = b.maxLng - b.minLng;
    if (width > MAX_VIEWPORT_WIDTH) {
      setZones(null);
      return;
    }

    // Check if we've moved far enough from the last fetch to warrant a new one
    const centerLng = (b.minLng + b.maxLng) / 2;
    const centerLat = (b.minLat + b.maxLat) / 2;
    if (lastFetchedCenter.current) {
      const dLng = Math.abs(centerLng - lastFetchedCenter.current.lng);
      const dLat = Math.abs(centerLat - lastFetchedCenter.current.lat);
      if (dLng < REFETCH_THRESHOLD && dLat < REFETCH_THRESHOLD) {
        // Reuse cached data — viewport hasn't moved enough
        return;
      }
    }

    const myId = ++reqId.current;
    setLoading(true);
    try {
      const data = await getZonesInBbox(b.minLng, b.minLat, b.maxLng, b.maxLat);
      if (myId !== reqId.current) return;
      setZones(data);
      lastFetchedCenter.current = { lng: centerLng, lat: centerLat };
    } catch {
      // Zones are non-critical; silently skip on error.
      if (myId === reqId.current) {
        setZones(null);
      }
    } finally {
      if (myId === reqId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!bounds) return;
    const t = window.setTimeout(() => void fetchZones(bounds), DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [bounds, fetchZones]);

  return {
    zones,
    loading,
    setBounds: setBoundsState,
  };
}
